import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faHome,
  faReceipt,
  faCreditCard,
  faCalendarAlt,
  faMoneyBillWave,
  faArrowRight,
  faDownload,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";

const BillingSuccess = () => {
  const navigate = useNavigate();
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Get transaction details from localStorage
    const details = localStorage.getItem("transactionDetails");
    if (details) {
      setTransactionDetails(JSON.parse(details));
    }

    // Hide confetti after animation
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "0";
    return amount.toLocaleString();
  };

  const getPaymentMethodName = (method) => {
    const methodMap = {
      visa: "Visa",
      mastercard: "Mastercard",
      unionpay: "UnionPay",
      jcb: "JCB",
      mpu: "MPU",
      kbzpay: "KBZPay",
      wavepay: "WavePay",
      ayapay: "AYA Pay",
      cbpay: "CB Pay",
    };
    return methodMap[method] || method || "N/A";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 relative overflow-hidden">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                backgroundColor: [
                  "#10B981",
                  "#3B82F6",
                  "#F59E0B",
                  "#EF4444",
                  "#8B5CF6",
                ][Math.floor(Math.random() * 5)],
                borderRadius: Math.random() > 0.5 ? "50%" : "0",
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${Math.random() * 2 + 2}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="max-w-2xl w-full">
          {/* Success Card */}
          <div className="bg-white rounded-3xl shadow-2xl border border-green-100 overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center text-white relative overflow-hidden">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                {/* Animated Checkmark */}
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-once shadow-lg">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="w-16 h-16 text-green-500"
                  />
                </div>

                <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                  Payment Successful!
                </h1>
                <p className="text-green-100 text-lg">
                  Thank you for your payment. Your transaction has been
                  completed successfully.
                </p>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="p-6 sm:p-8">
              <div className="space-y-4">
                {/* Transaction ID */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faReceipt}
                        className="w-5 h-5 text-blue-600"
                      />
                    </div>
                    <span className="text-gray-600 font-medium">
                      Transaction ID
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold">
                    {transactionDetails?.transactionId || "N/A"}
                  </span>
                </div>

                {/* Amount */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faMoneyBillWave}
                        className="w-5 h-5 text-green-600"
                      />
                    </div>
                    <span className="text-gray-600 font-medium">
                      Amount Paid
                    </span>
                  </div>
                  <span className="text-gray-900 font-bold text-xl">
                    {formatAmount(transactionDetails?.amount)}{" "}
                    {transactionDetails?.currency?.toUpperCase() || "MMK"}
                  </span>
                </div>

                {/* Payment Method */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faCreditCard}
                        className="w-5 h-5 text-purple-600"
                      />
                    </div>
                    <span className="text-gray-600 font-medium">
                      Payment Method
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold">
                    {getPaymentMethodName(transactionDetails?.paymentMethod)}
                  </span>
                </div>

                {/* Date & Time */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="w-5 h-5 text-orange-600"
                      />
                    </div>
                    <span className="text-gray-600 font-medium">
                      Date & Time
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold">
                    {formatDate(transactionDetails?.timestamp)}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-6" />

              {/* What's Next Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="w-5 h-5 text-blue-600"
                  />
                  What's Next?
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>You will receive a confirmation email shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>
                      Your account has been upgraded with the selected features
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>
                      Access your new features from the merchant portal
                    </span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <FontAwesomeIcon icon={faHome} className="w-5 h-5" />
                  Back to Home
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faDownload} className="w-5 h-5" />
                  Download Receipt
                </button>
              </div>

              {/* Share Section */}
              <div className="mt-6 text-center">
                <button className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors">
                  <FontAwesomeIcon icon={faShareAlt} className="w-4 h-4" />
                  <span className="text-sm">Share this receipt</span>
                </button>
              </div>
            </div>
          </div>

          {/* Support Section */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Need help?{" "}
              <a
                href="mailto:support@shopnest.com"
                className="text-blue-600 hover:underline font-medium"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Confetti Animation Styles */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti-fall linear forwards;
        }
        @keyframes bounce-once {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
        .animate-bounce-once {
          animation: bounce-once 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default BillingSuccess;
